/**
 * Shared pieces of the Licence margin tabs (#1142): stat strip, filter chips, banded values with hover detail, the
 * legend, the grouped sortable table and the cost sparkline. Patchbay tokens only; status colours always come with the
 * number and words (the value itself, plus the band named in the hover and the accessible name).
 */
import { cn, Tooltip } from '@patchbay/ui'
import * as React from 'react'
import { AppLink } from '../shared'
import {
  type ChipDef,
  MARGIN_BANDS,
  MARGIN_LABEL,
  type MarginBand,
  MSRP_BANDS,
  MSRP_LABEL,
  type MsrpBand,
  marginBand,
  money,
  msrpBand,
  type Notes,
  pct,
  type SortState,
  signed,
  type Total,
} from './model'

export const DASH = '—'

/** A missing value: a muted dash with words for screen readers, never a 0. */
export function Missing({ words = 'unknown' }: { words?: string }) {
  return (
    <span className="text-muted-foreground" data-missing>
      <span aria-hidden>{DASH}</span>
      <span className="sr-only">{words}</span>
    </span>
  )
}

export function MaybeLink({ href, children, className }: { href?: string | null; children: React.ReactNode; className?: string }) {
  if (!href) return <span className={className}>{children}</span>
  return (
    <AppLink
      href={href}
      className={cn('underline decoration-border underline-offset-2 hover:text-primary hover:decoration-primary', className)}
    >
      {children}
    </AppLink>
  )
}

// --- stat strip -----------------------------------------------------------------------------------------------------

export type StatTone = 'bad' | 'warn' | undefined

export interface Stat {
  label: string
  /** The formatted value, or null when it cannot be known (renders "Unknown", never 0). */
  value: string | null
  tone?: StatTone
  /** Small print under the value (e.g. "3 lines unpriced"). */
  note?: string | null
}

export function StatStrip({ stats, label }: { stats: Stat[]; label: string }) {
  return (
    <section aria-label={label} className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-[repeat(auto-fit,minmax(9.5rem,1fr))]">
      {stats.map((s) => (
        <div key={s.label} data-stat={s.label} className="flex min-w-0 flex-col gap-1 rounded-lg border border-border bg-card p-3">
          <span className="text-xs font-medium text-muted-foreground">{s.label}</span>
          {s.value === null ? (
            <span className="text-sm font-medium text-muted-foreground">Unknown</span>
          ) : (
            <span
              data-stat-value
              className={cn(
                'tabular text-xl font-semibold',
                s.tone === 'bad' ? 'text-destructive' : s.tone === 'warn' ? 'text-warning' : 'text-foreground',
              )}
            >
              {s.value}
            </span>
          )}
          {s.note ? <span className="text-xs text-muted-foreground">{s.note}</span> : null}
        </div>
      ))}
    </section>
  )
}

/** A money total as a stat value, with the count of lines it could not price. */
export function moneyStat(label: string, t: Total, tone?: StatTone): Stat {
  return {
    label,
    value: t.value === null ? null : money(t.value),
    tone,
    note: t.value !== null && t.missing ? `${t.missing} line${t.missing === 1 ? '' : 's'} not priced` : null,
  }
}

export const pctStat = (label: string, v: number | null, tone?: StatTone): Stat => ({ label, value: v === null ? null : pct(v), tone })
export const countStat = (label: string, n: number, tone?: StatTone): Stat => ({ label, value: String(n), tone: n ? tone : undefined })

// --- chips ----------------------------------------------------------------------------------------------------------

export function ChipButton({
  pressed,
  onClick,
  children,
  count,
}: {
  pressed: boolean
  onClick: () => void
  children: React.ReactNode
  count?: number
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        'inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-md border px-2 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-ring',
        pressed ? 'border-primary bg-primary-soft text-primary' : 'border-border bg-card text-foreground hover:bg-accent',
      )}
    >
      {children}
      {count === undefined ? null : (
        <>
          {' '}
          <span className="tabular text-muted-foreground">{count}</span>
        </>
      )}
    </button>
  )
}

export function ChipFilter<T>({
  defs,
  rows,
  notes,
  value,
  onChange,
  label,
}: {
  defs: ChipDef<T>[]
  rows: T[]
  notes: Notes
  value: string
  onChange: (id: string) => void
  label: string
}) {
  return (
    <fieldset className="flex flex-wrap items-center gap-1.5" aria-label={label}>
      <legend className="sr-only">{label}</legend>
      {defs.map((d) => (
        <ChipButton key={d.id} pressed={value === d.id} onClick={() => onChange(d.id)} count={rows.filter((r) => d.test(r, notes)).length}>
          {d.label}
        </ChipButton>
      ))}
    </fieldset>
  )
}

// --- banded values --------------------------------------------------------------------------------------------------

export const BAND_CLASS: Record<MarginBand, string> = {
  negative: 'bg-destructive-soft text-destructive',
  u5: 'bg-warning-soft text-warning',
  u10: 'bg-card text-warning ring-1 ring-inset ring-warning/40',
  u20: 'bg-muted text-foreground',
  ok: 'bg-success-soft text-success',
}

/** A coloured value; the detail lines show on hover or keyboard focus, and are part of its accessible name. */
export function BandValue({ text, band, lines }: { text: string; band: MarginBand; lines: string[] }) {
  return (
    <Tooltip
      content={
        <div className="grid gap-0.5 text-left" data-testid="band-detail">
          {lines.map((l) => (
            <div key={l}>{l}</div>
          ))}
        </div>
      }
    >
      <button
        type="button"
        data-band={band}
        aria-label={[text, ...lines].join('. ')}
        className={cn(
          'tabular inline-block cursor-help rounded-sm px-1.5 py-px font-medium focus-visible:outline-2 focus-visible:outline-ring',
          BAND_CLASS[band],
        )}
      >
        {text}
      </button>
    </Tooltip>
  )
}

export function MarginValue({ value, lines = [] }: { value: number | null; lines?: string[] }) {
  const band = marginBand(value)
  if (value === null || band === null) return <Missing />
  return <BandValue text={pct(value)} band={band} lines={[MARGIN_LABEL[band], ...lines]} />
}

const MSRP_TO_CLASS: Record<MsrpBand, MarginBand> = { ok: 'ok', u10: 'u10', u5: 'u5', negative: 'negative' }

export function MsrpValue({
  pctOfMsrp,
  detail,
}: {
  pctOfMsrp: number | null
  detail?: { bill: number; msrp: number; unit: string } | null
}) {
  const b = msrpBand(pctOfMsrp)
  if (!b) return <Missing />
  const lines = [MSRP_LABEL[b.band]]
  if (detail)
    lines.push(
      `We bill ${money(detail.bill)}, MSRP ${money(detail.msrp)} ${detail.unit}`,
      `Difference ${signed(detail.bill - detail.msrp, money)}`,
    )
  return <BandValue text={signed(b.diff, pct)} band={MSRP_TO_CLASS[b.band]} lines={lines} />
}

export function Legend({ showMsrp = true, marginTitle = 'Margin' }: { showMsrp?: boolean; marginTitle?: string }) {
  const swatch = (band: MarginBand, label: string) => (
    <span key={label} className={cn('rounded-sm px-1.5 py-px text-2xs font-medium', BAND_CLASS[band])}>
      {label}
    </span>
  )
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-muted-foreground" data-legend>
      <span className="flex flex-wrap items-center gap-1.5">
        {marginTitle}
        {MARGIN_BANDS.map((b) => swatch(b.band, b.label))}
      </span>
      {showMsrp ? (
        <span className="flex flex-wrap items-center gap-1.5">
          ± MSRP
          {MSRP_BANDS.map((b) => swatch(MSRP_TO_CLASS[b.band], b.label))}
        </span>
      ) : null}
      <span>Hover or tab to a number for detail.</span>
    </div>
  )
}

// --- grouped, sortable table ----------------------------------------------------------------------------------------

export interface Column {
  /** Sort key; null for an unsortable column. */
  key: string | null
  label: string
  /** Group header; consecutive columns with the same group share it. Empty = no group label. */
  group: string
  numeric?: boolean
  /** Hidden on a phone. */
  narrow?: boolean
}

/** Per-column classes: a left rule where a group starts, and a quiet shade on alternate labelled groups. */
export function groupClasses(cols: Column[]): string[] {
  let prev: string | symbol = Symbol('start')
  let n = -1
  return cols.map((c) => {
    const start = c.group !== prev
    if (start) {
      n++
      prev = c.group
    }
    return cn(start && n > 0 && 'border-l border-border', c.group.trim() && n % 2 === 1 && 'bg-muted/40', c.narrow && 'max-sm:hidden')
  })
}

export function GroupedTable({
  columns,
  sort,
  onSort,
  label,
  children,
  minWidth = 1100,
}: {
  columns: Column[]
  sort: SortState
  onSort: (s: SortState) => void
  label: string
  children: React.ReactNode
  minWidth?: number
}) {
  const cls = groupClasses(columns)
  const groups: { label: string; span: number; cls: string; narrow: boolean }[] = []
  columns.forEach((c, i) => {
    const last = groups[groups.length - 1]
    if (i > 0 && columns[i - 1]?.group === c.group && last) {
      last.span++
      last.narrow = last.narrow && !!c.narrow
    } else groups.push({ label: c.group, span: 1, cls: cls[i] ?? '', narrow: !!c.narrow })
  })
  return (
    <div className="max-h-[72vh] overflow-auto rounded-xl border border-border bg-card">
      <table className="w-full text-sm" style={{ minWidth }} aria-label={label}>
        <thead>
          <tr>
            {groups.map((g, i) => (
              <th
                // biome-ignore lint/suspicious/noArrayIndexKey: groups are positional
                key={i}
                colSpan={g.span}
                scope="colgroup"
                className={cn(
                  'sticky top-0 z-[2] h-7 bg-card px-2 text-center text-2xs font-semibold uppercase tracking-wide',
                  g.label.trim() ? 'border-b-2 border-b-primary text-primary' : 'text-muted-foreground',
                  g.cls,
                  g.narrow && 'max-sm:hidden',
                )}
              >
                {g.label.trim() ? g.label : <span className="sr-only">{i === groups.length - 1 ? 'Notes' : 'Line'}</span>}
              </th>
            ))}
          </tr>
          <tr className="text-xs text-muted-foreground">
            {columns.map((c, i) => {
              const active = c.key !== null && sort.key === c.key
              return (
                <th
                  key={`${c.group}:${c.label}`}
                  scope="col"
                  aria-sort={active ? (sort.dir > 0 ? 'ascending' : 'descending') : undefined}
                  className={cn(
                    'sticky top-7 z-[2] border-b border-border bg-card font-medium whitespace-nowrap',
                    c.numeric && 'text-right',
                    cls[i],
                  )}
                >
                  {c.key === null ? (
                    <span className="block px-2 py-2">{c.label}</span>
                  ) : (
                    <button
                      type="button"
                      className={cn(
                        'block w-full cursor-pointer px-2 py-2 hover:text-foreground',
                        c.numeric ? 'text-right' : 'text-left',
                        active && 'text-foreground',
                      )}
                      onClick={() => onSort({ key: c.key as string, dir: active ? (sort.dir > 0 ? -1 : 1) : 1 })}
                    >
                      {c.label}
                      {active ? <span aria-hidden>{sort.dir > 0 ? ' ↑' : ' ↓'}</span> : null}
                    </button>
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

export const cellBase = 'border-b border-border/60 px-2 py-1.5 align-top'
export const numCell = 'tabular text-right whitespace-nowrap'

// --- sparkline ------------------------------------------------------------------------------------------------------

/** Cost per month, oldest first: a 2 px line in the first chart slot, the latest month marked. */
export function Sparkline({ values, months }: { values: (number | null)[]; months: string[] }) {
  const known = values.filter((v): v is number => v !== null)
  if (values.length < 2 || known.length < 2) return <Missing words="no trend" />
  const w = 84
  const h = 24
  const max = Math.max(...known, 0.01)
  const pts = values.map((v, i) => (v === null ? null : ([3 + (i * (w - 6)) / (values.length - 1), h - 4 - (v / max) * (h - 8)] as const)))
  const segs = pts.filter((p): p is readonly [number, number] => p !== null)
  const last = segs[segs.length - 1]
  const words = values.map((v, i) => `${months[i] ?? `month ${i + 1}`} ${v === null ? 'unknown' : money(v)}`)
  return (
    <Tooltip
      content={
        <div className="grid gap-0.5">
          {words.map((l) => (
            <div key={l}>{l}</div>
          ))}
        </div>
      }
    >
      <span
        // biome-ignore lint/a11y/noNoninteractiveTabindex: the per-month figures must be reachable by keyboard too
        tabIndex={0}
        role="img"
        aria-label={`Cost by month: ${words.join(', ')}`}
        data-sparkline
        className="inline-block cursor-help rounded-sm focus-visible:outline-2 focus-visible:outline-ring"
      >
        <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} aria-hidden className="block overflow-visible">
          <polyline
            points={segs.map((p) => p.join(',')).join(' ')}
            fill="none"
            stroke="var(--chart-1)"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            data-mark-paint
          />
          {last ? <circle cx={last[0]} cy={last[1]} r={3} fill="var(--chart-1)" stroke="var(--color-card)" strokeWidth={2} /> : null}
        </svg>
      </span>
    </Tooltip>
  )
}

export function SearchBox({
  value,
  onChange,
  label,
  count,
}: {
  value: string
  onChange: (v: string) => void
  label: string
  count: string
}) {
  const id = React.useId()
  return (
    <div className="flex flex-wrap items-center gap-3">
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <input
        id={id}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search customer, licence, note"
        className="h-8 w-full max-w-xs min-w-0 rounded-md border border-input bg-card px-2.5 text-sm text-foreground outline-none placeholder:text-subtle focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/25"
      />
      <span className="text-xs text-muted-foreground" aria-live="polite">
        {count}
      </span>
    </div>
  )
}
